import { useParams } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import {
  rSimInventoryDetails,
  rSimInventoryDetailsScrollArea,
  tagGridSizes,
} from './RSimInventoryDetailsPage.style';
import { ContentCopyExtension } from 'common/ContentCopyExtension/ContentCopyExtension';
import { SubscriptionStatusBadge } from 'src/common/components/ConfigurableTable/Components/Badges/SubscriptionStatusBadge';
import Stack from '@mui/material/Stack';
import { TagDisplay } from 'tags/TagsDisplay';
import { Tag } from 'tags/models/tagsApi.types';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import { ValueBox } from 'common/components/Table/TwoValueCell';
import ConfigurationIcon from 'assets/images/configuration.svg';
import PendingIcon from 'assets/images/pending.svg';
import { SectionGridRow } from '@/common/components/SectionGrid/SectionGridRow';
import { SectionGridItem } from '@/common/components/SectionGrid/SectionGridItem';
import { RSimInventorySimDetails } from './RSimInventorySimDetails';
import { RefetchQueryButton } from 'common/components/Queries/RefetchQueryButton';
import { Actions, Subjects } from 'permissions/ability';
import { SimInventoryCan } from 'permissions/PermissionProvider';
import { useRSimSuspenseQuery } from 'rSimInventory/hooks/useRSimSuspenseQuery';
import { OrderSection } from 'common/components/Order/Details/OrderSection';
import { DeviceSection } from 'common/components/Device/Details/DeviceSection';
import { useRSimDeviceData } from './hooks/useRSimDeviceData';
import { rsimProductDetailsQueries } from './utils/rsimProductDetailsQueries';
import { useRSimSimProfile } from './hooks/useRSimSimProfile';
import { RSimDetailsEdit } from './RSimDetailsEdit';
import { KickRsimProvider } from '../Components/KickRsimProvider';
import { RSimStatusIndicator } from '../Components/RSimStatusIndicator';
import { mapValues } from 'lodash';
import {
  RSIM_CLIENT_SECTION_PROPS,
  RSIM_PROFILES_SECTION_PROPS,
  RSIM_USAGE_IN_CYCLE_SECTION_PROPS,
} from './constants/sections';
import { DisplaySection } from 'src/common/components/Sim/Details/DisplaySection';
import { SUBSCRIPTION_STATUS } from 'src/common/components/SubscriptionStatus/constants/subscriptionStatus';
import { ScrollArea } from 'src/common/components/ScrollArea/ScrollArea';
import { RSimLastPooling } from './fields/RSimLastPooling/RSimLastPooling';
import { useHasFeatureFlag } from 'src/featureFlags/useHasFeatureFlag';
import {
  DetailsType,
  UsageChartSection,
} from 'src/common/components/Sim/Details/UsageChartsSection';
import { detailsQueries } from 'src/common/components/Sim/Details/queries/detailsQueries';
import { pxToRem } from 'src/common/utils/fonts';
import { CustomerCommsPlanDetail } from './components/CustomerCommsPlanDetail';
import { RSimLastSessionDetails } from './components/LastSessionDetails';
import { useLastSessionInfo } from './hooks/useLastSessionInfo';
import { SimData } from 'src/common/components/Sim/constants/sim';
import { SectionGridNumericItem } from 'src/common/components/SectionGrid/SectionGridNumericItem';
import { SectionGriDurationItem } from 'src/common/components/SectionGrid/SectionGriDurationItem';
import { OtaConfigurationDetails } from './components/OtaConfigurationDetails';

export type SimInventoryEntryRoutingParams = {
  euicc: string;
};

const mapProfileValues = (data: SimData) =>
  mapValues(data, (value) => {
    if (value === '') {
      return '-';
    }

    return value;
  });

export const RSimInventoryDetailsPage = () => {
  const rsimConnectivityMonitoringEnabled = useHasFeatureFlag('RsimConnectivityMonitoringEnabled');

  const { t } = useTranslation();
  const { euicc } = useParams<SimInventoryEntryRoutingParams>();
  if (!euicc) {
    throw new Error('euicc not defined');
  }

  const { data: rSimInventory } = useRSimSuspenseQuery(euicc);

  const rSim = rSimInventory?.items[0];

  const { data: primarySimEntry } = useRSimSimProfile(
    { euicc: euicc, iccid: rSim?.primaryIccid },
    mapProfileValues,
  );

  const { data: secondarySimEntry } = useRSimSimProfile(
    {
      euicc: euicc,
      iccid: rSim?.secondaryIccid,
    },
    mapProfileValues,
  );

  const { simWithLastSession, simNameWithLastSession } = useLastSessionInfo({
    primarySimEntry,
    secondarySimEntry,
  });

  const deviceData = useRSimDeviceData({
    primarySim: primarySimEntry,
    backupSim: secondarySimEntry,
    simWithLastSession,
  });

  return (
    <Stack sx={rSimInventoryDetailsScrollArea}>
      <Box
        flex={0}
        sx={{
          display: 'flex',
          justifyContent: 'end',
          pt: 8,
          pr: 24,
          gap: 16,
        }}
      >
        <OtaConfigurationDetails otaRsimDetails={rSim} euicc={euicc} />
        <Box>
          <RefetchQueryButton queryKey={detailsQueries.details} />
        </Box>
        <SimInventoryCan I={Actions.edit} a={Subjects.tags}>
          <RSimDetailsEdit euicc={euicc} />
        </SimInventoryCan>
      </Box>
      <ScrollArea className="os-host-flexbox">
        <KickRsimProvider euiccs={[euicc]} queryKey={rsimProductDetailsQueries.all}>
          <Box sx={rSimInventoryDetails}>
            <DisplaySection data={rSim} keys={RSIM_PROFILES_SECTION_PROPS}>
              <SectionGridRow
                hideTopBorder={true}
                sx={{
                  pl: 12,
                }}
                title={t('rSimInventory.title')}
              >
                <>
                  <SectionGridItem label={t('rSimInventory.rsimStatus')} testId="rsimStatus">
                    <RSimStatusIndicator euicc={euicc} status={rSim?.status} hideTooltip={true} />
                  </SectionGridItem>
                  {rsimConnectivityMonitoringEnabled ? (
                    <RSimLastPooling euicc={euicc} />
                  ) : (
                    <SectionGridItem
                      value={rSim?.lastPollingTimestamp}
                      label={t('rSimInventory.lastPolling')}
                      testId="lastPolling"
                    />
                  )}
                  <SectionGridItem
                    label={t('rSimInventory.subscriptionStatus')}
                    testId="subscriptionStatus"
                  >
                    <SubscriptionStatusBadge
                      subscriptionStatus={rSim?.subscriptionStatus ?? SUBSCRIPTION_STATUS.UNKNOWN}
                    />
                  </SectionGridItem>
                  <SectionGridItem label={t('rSimInventory.configuration')}>
                    <Box
                      sx={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
                      data-testid="configuration"
                    >
                      {rSim?.isCustomConfigurationDetected && (
                        <Tooltip title={t('rSimInventory.customConfigurationDetected')}>
                          <ConfigurationIcon
                            width={pxToRem(16)}
                            height={pxToRem(16)}
                            role="img"
                            data-testid="custom-configuration-icon"
                          />
                        </Tooltip>
                      )}

                      {rSim?.isPendingConfiguration && (
                        <Tooltip title={t('rSimInventory.configurationChangePending')}>
                          <PendingIcon
                            width={pxToRem(18)}
                            height={pxToRem(18)}
                            role="img"
                            data-testid="change-pending-icon"
                          />
                        </Tooltip>
                      )}
                      <ContentCopyExtension value={rSim?.appliedOtaConfigurationProfile || '-'}>
                        <Typography
                          marginRight={
                            rSim?.isCustomConfigurationDetected || rSim?.isPendingConfiguration
                              ? 5
                              : ''
                          }
                          marginLeft={5}
                          variant="text1"
                          color="text.primary"
                          data-testid="configuration-profile"
                        >
                          {rSim?.appliedOtaConfigurationProfile || '-'}
                        </Typography>
                      </ContentCopyExtension>

                      <ValueBox
                        variant="text5"
                        color="text.secondary"
                        text={rSim?.appliedOtaConfigurationVersion || ''}
                        testId="configuration-version"
                      />
                    </Box>
                  </SectionGridItem>
                  <SectionGridItem value={rSim?.euicc} label={t('common.eid')} testId="euicc" />
                  <SimInventoryCan I={Actions.read} a={Subjects.otaConfiguration}>
                    <SectionGridItem
                      value={rSim?.iccidOta}
                      label={t('rSimInventory.otaIccid')}
                      testId="otaIccid"
                    />
                  </SimInventoryCan>
                </>
              </SectionGridRow>
              <SectionGridRow
                hideTopBorder={true}
                sx={{
                  pb: 12,
                  pl: 12,
                }}
              >
                <SectionGridItem {...tagGridSizes} label={t('common.tags')} testId="tags">
                  {!!rSim?.tags?.items && rSim?.tags?.items.length > 0 ? (
                    <TagDisplay
                      id={euicc}
                      initialTags={rSim?.tags?.items.map((item: Tag) => item.name) || []}
                      hasMore={rSim?.tags?.hasMore || false}
                      shouldWrap={true}
                    />
                  ) : (
                    <span>-</span>
                  )}
                </SectionGridItem>
              </SectionGridRow>
            </DisplaySection>
            <DeviceSection deviceData={deviceData} />
            {rSim && <OrderSection orderData={rSim} />}
            <DisplaySection data={rSim} keys={RSIM_CLIENT_SECTION_PROPS}>
              <SectionGridRow title={t('simInventory.client')}>
                <>
                  <SectionGridItem
                    value={rSim?.accountName}
                    label={t('common.accountName')}
                    testId="accountName"
                  />
                  <SectionGridItem
                    value={rSim?.accountNumber}
                    label={t('common.accountNumber')}
                    testId="accountNumber"
                  />
                  <SectionGridItem
                    value={rSim?.installerName}
                    label={t('simInventory.organisationName')}
                    testId="installerName"
                  />
                  <SectionGridItem
                    value={
                      typeof rSim?.installerRef === 'number'
                        ? rSim?.installerRef?.toString()
                        : rSim?.installerRef
                    }
                    label={t('simInventory.organisationNumber')}
                    testId="installerRef"
                  />
                  <CustomerCommsPlanDetail
                    primarySimEntry={primarySimEntry}
                    secondarySimEntry={secondarySimEntry}
                    lastActiveProfile={rSim.lastActiveProfile}
                  />
                </>
              </SectionGridRow>
            </DisplaySection>
            <DisplaySection data={rSim} keys={RSIM_USAGE_IN_CYCLE_SECTION_PROPS}>
              <SectionGridRow title={t('simInventory.usageInCycle')}>
                <>
                  <SectionGridNumericItem
                    value={rSim?.dataUsage}
                    label={t('common.data')}
                    testId="dataUsage"
                  />
                  <SectionGriDurationItem
                    value={rSim?.inMinuteUsage}
                    label={t('common.inMinuteUsageProduct')}
                    testId="inVoiceUsage"
                  />
                  <SectionGridNumericItem
                    value={rSim?.inTextUsage}
                    label={t('common.inTextUsageProduct')}
                    testId="inSmsUsage"
                  />
                  <SectionGriDurationItem
                    value={rSim?.outMinuteUsage}
                    label={t('common.outMinuteUsageProduct')}
                    testId="outVoiceUsage"
                  />
                  <SectionGridNumericItem
                    value={rSim?.outTextUsage}
                    label={t('common.outTextUsageProduct')}
                    testId="outSmsUsage"
                  />
                </>
              </SectionGridRow>
            </DisplaySection>
            <UsageChartSection detailsType={DetailsType.RSim} fetchBy={rSim.euicc} />
            <RSimLastSessionDetails
              simNameWithLastSession={simNameWithLastSession}
              simWithLastSession={simWithLastSession}
              primarySimEntry={primarySimEntry}
              secondarySimEntry={secondarySimEntry}
            />
            <RSimInventorySimDetails
              primarySimEntry={primarySimEntry}
              secondarySimEntry={secondarySimEntry}
              rSimStatus={rSim?.status}
              euicc={euicc}
            />
          </Box>
        </KickRsimProvider>
      </ScrollArea>
    </Stack>
  );
};
